import {
  canNestAt,
  ROOT_FOLDER_ID,
  type FileSystemNode,
  type FolderCrumb,
  type FolderNode,
} from '../../types/fileSystem.js';
import { isUnprocessedStatus } from '../ragDocumentStatus.js';
import { hasSiblingNamed, isFolderNode } from './fileSystemNode.js';
import {
  canManageFolder,
  type KnowledgeBaseAccess,
} from './knowledgeBaseAccess.js';

export type MoveRejection =
  | 'root-not-allowed'
  | 'empty-selection'
  | 'self'
  | 'descendant'
  | 'same-parent'
  | 'max-depth'
  | 'name-taken'
  | 'file-not-ready'
  | 'no-permission';

export type MoveVerdict =
  | { allowed: true }
  | { allowed: false; reason: MoveRejection };

export type MoveSource = {
  currentFolderId: string;
  access: KnowledgeBaseAccess;
};

export type MoveTarget = {
  folderId: string;
  breadcrumb: FolderCrumb[];
  folders: FolderNode[];
};

const ALLOWED: MoveVerdict = { allowed: true };

const deny = (reason: MoveRejection): MoveVerdict => ({
  allowed: false,
  reason,
});

const isMovable = (node: FileSystemNode, { access }: MoveSource) =>
  isFolderNode(node)
    ? !node.isBucket && canManageFolder(node, access)
    : access.canWrite;

export const canMoveSource = (
  node: FileSystemNode,
  source: MoveSource,
): MoveVerdict => {
  if (!isFolderNode(node) && isUnprocessedStatus(node.status)) {
    return deny('file-not-ready');
  }

  if (!isMovable(node, source)) return deny('no-permission');

  return ALLOWED;
};

const canMoveNode = (
  node: FileSystemNode,
  target: MoveTarget,
  source: MoveSource,
): MoveVerdict => {
  const verdict = canMoveSource(node, source);

  if (!verdict.allowed) return verdict;

  if (isFolderNode(node)) {
    if (node.id === target.folderId) return deny('self');

    if (target.breadcrumb.some(({ folderId }) => folderId === node.id)) {
      return deny('descendant');
    }
  }

  // Files are listed one folder at a time, so the open folder *is* the parent
  // of every file the client currently holds.
  const parentId = isFolderNode(node) ? node.parentId : source.currentFolderId;

  if (parentId === target.folderId) return deny('same-parent');

  // Only the target's own depth is known here; how deep the moved folder's
  // subtree goes is the server's call.
  if (isFolderNode(node) && !canNestAt(target.breadcrumb.length)) {
    return deny('max-depth');
  }

  if (
    isFolderNode(node) &&
    hasSiblingNamed(target.folders, node.name, node.id)
  ) {
    return deny('name-taken');
  }

  return ALLOWED;
};

export const canMoveInto = (
  nodes: FileSystemNode[],
  target: MoveTarget,
  source: MoveSource,
): MoveVerdict => {
  if (!nodes.length) return deny('empty-selection');

  // A row at depth 0 is a bucket, and the API has no way to ask for one: both
  // move endpoints require a real parent id.
  if (target.folderId === ROOT_FOLDER_ID) return deny('root-not-allowed');

  for (const node of nodes) {
    const verdict = canMoveNode(node, target, source);

    if (!verdict.allowed) return verdict;
  }

  return ALLOWED;
};
